import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Box, Card, CardContent, Drawer, Fab, IconButton, Typography, useTheme, Tooltip, Chip, Stack, Button } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { MessageOutlined, CloseOutlined } from '@ant-design/icons';
import SimpleBar from 'components/third-party/SimpleBar';
import PremiumChatInput from 'components/chat/PremiumChatInput';
import { startResearch, openResearchStream } from 'services/researchService';
import { startChat, openChatStream, chatSimple } from 'services/chatService';
import { orchestrateAgent, startAgentStream, openAgentStream } from 'services/agentService';
import { setChatContext, clearChatContexts, removeChatContext, setChatOpen } from 'store/reducers/chatContext';

const ChatMessage = ({ message, theme }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
      mb: 2
    }}
  >
    <Card
      sx={{
        maxWidth: '85%',
        background: message.type === 'user' 
          ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
          : theme.palette.grey[100],
        color: message.type === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
        borderRadius: message.type === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
        boxShadow: `0 2px 10px ${theme.palette.action.hover}`,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="body1">{message.content}</Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            opacity: 0.7, 
            display: 'block', 
            mt: 1,
            fontSize: '0.75rem'
          }}
        >
          {new Date(message.timestamp).toLocaleTimeString()}
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

const ContextMessage = ({ context, theme, onRemove }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'flex-start',
      mb: 2
    }}
  >
    <Card
      sx={{
        maxWidth: '85%',
        background: `linear-gradient(135deg, ${theme.palette.info.light}, ${theme.palette.info.main})`,
        color: theme.palette.info.contrastText,
        borderRadius: '20px 20px 20px 4px',
        boxShadow: `0 2px 10px ${theme.palette.action.hover}`,
        border: `1px solid ${theme.palette.info.main}`,
        position: 'relative',
      }}
    >
      <IconButton
        onClick={() => onRemove(context.id)}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          color: theme.palette.info.contrastText,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          width: 20,
          height: 20,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        <CloseOutlined style={{ fontSize: '12px' }} />
      </IconButton>
      <CardContent sx={{ p: 2, pr: 4, '&:last-child': { pb: 2 } }}>
        <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 'bold' }}>
          {context.selectionType === 'text' ? '📝 Text Selected' : '📋 Card Selected'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, maxHeight: '100px', overflow: 'auto' }}>
          {context.content}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            opacity: 0.7, 
            display: 'block', 
            mt: 1,
            fontSize: '0.75rem'
          }}
        >
          {new Date(context.timestamp).toLocaleTimeString()}
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

const ChatSidebar = ({ onToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showTyping, setShowTyping] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeStream, setActiveStream] = useState(null);
  const [activeMode, setActiveMode] = useState('deep_research'); // 'deep_research' | 'chat' | 'agent' | 'agent_stream'
  const [isStreaming, setIsStreaming] = useState(true); // used only for 'chat' mode
  const [thoughtBuffer, setThoughtBuffer] = useState('');
  const [showThoughts, setShowThoughts] = useState(true);
  const [answerStarted, setAnswerStarted] = useState(false);
  const [hasStreamedContent, setHasStreamedContent] = useState(false);
  const chatContext = useSelector((state) => state.chatContext.context);
  const chatContexts = useSelector((state) => state.chatContext.contexts);
  const messagesEndRef = React.useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldShow = params.get('showChat') === 'true';
    setIsVisible(shouldShow);
    if (!shouldShow) {
      setIsOpen(false);
      onToggle(false);
    }
  }, [location.search, onToggle]);
  
  useEffect(() => {
    onToggle(isOpen);
    dispatch(setChatOpen(isOpen));
  }, [isOpen, onToggle, dispatch]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleRemoveContext = (contextId) => {
    dispatch(removeChatContext(contextId));
  };

  const handleSendMessage = async (message) => {
    console.log('[ChatSidebar] handleSendMessage START, activeMode:', activeMode);
    // Combine all contexts into the message
    let userMessage = message;
    
    if (chatContexts.length > 0) {
      const contextParts = chatContexts.map((ctx, idx) => `Context ${idx + 1}: "${ctx.content}"`).join('\n\n');
      userMessage = `${contextParts}\n\nMessage: "${message}"`;
    } else if (chatContext) {
      // Fallback to old context for backward compatibility
      userMessage = `Context: "${chatContext}"\n\nMessage: "${message}"`;
    }
    
    const newMessage = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: Date.now()
    };
    
    setMessages(prev => [...prev, newMessage]);

    // Handle different modes
    try {
      setShowTyping(true);
      setThoughtBuffer('');
      setShowThoughts(true);
      setAnswerStarted(false);
      setHasStreamedContent(false);

      if (activeMode === 'agent') {
        // Agent mode (non-streaming)
        const messageHistory = messages.map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.content
        }));

        const response = await orchestrateAgent({ content: message, messages: messageHistory });

        setShowTyping(false);
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: response?.message?.content || response?.result || 'No response received',
          timestamp: Date.now()
        }]);
        return;
      }

      if (activeMode === 'chat' && !isStreaming) {
        // Non-streaming request-response mode
        const messageHistory = messages.map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.content
        }));

        const response = await chatSimple({
          query: message,
          messages: messageHistory
        });

        setShowTyping(false);
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: response.message?.content || 'No response received',
          timestamp: Date.now()
        }]);
        return;
      }

      // Streaming modes
      console.log('[ChatSidebar] About to fetch stream URL, activeMode:', activeMode);
      let streamUrl = null;
      if (activeMode === 'agent_stream') {
        console.log('[ChatSidebar] Calling startAgentStream');
        const messageHistory = messages.map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.content
        }));
        streamUrl = await startAgentStream({ content: message, messages: messageHistory });
        console.log('[ChatSidebar] startAgentStream returned streamUrl:', streamUrl);
      } else if (activeMode === 'deep_research') {
        console.log('[ChatSidebar] Calling startResearch');
        streamUrl = await startResearch({
          query: message,
          market: 'US',
          model: 'o4-mini-deep-research',
          show_thoughts: true
        });
      } else {
        console.log('[ChatSidebar] Calling startChat');
        streamUrl = await startChat({
          query: message,
          model: 'gpt-4o-mini'
        });
      }

      console.log('[ChatSidebar] Stream URL received:', streamUrl);
      if (!streamUrl) throw new Error('No stream URL returned');

      // Close previous stream if any
      if (activeStream) {
        activeStream.close();
      }

      // Ensure an AI message placeholder exists to stream into
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.type === 'ai' && last.streaming) return prev;
        return [
          ...prev,
          {
            id: Date.now() + 1,
            type: 'ai',
            content: '',
            timestamp: Date.now(),
            streaming: true
          }
        ];
      });
      setAnswerStarted(true);

      let es;
      if (activeMode === 'agent_stream') {
        es = await openAgentStream(streamUrl, {
        onOpen: () => {},
        onToken: (data) => {
          const tokenText = data?.text ?? data?.delta ?? data?.content ?? data?.message ?? '';
          const isThoughtToken = !!(data?.thought === true || data?.type === 'thought' || data?.channel === 'thought' || data?.kind === 'thought' || data?.role === 'system_thought');

          if (activeMode === 'deep_research' && !answerStarted && isThoughtToken) {
            if (!tokenText) return;
            setThoughtBuffer(prev => prev + tokenText);
            setShowThoughts(true);
            return;
          }

          if (tokenText) {
            setHasStreamedContent(true);
            if (!answerStarted) {
              setAnswerStarted(true);
              setShowThoughts(false);
            }
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last && last.type === 'ai' && last.streaming) {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  ...last,
                  content: (last.content || '') + tokenText
                };
                return updated;
              }
              return [
                ...prev,
                {
                  id: Date.now() + 1,
                  type: 'ai',
                  content: tokenText,
                  timestamp: Date.now(),
                  streaming: true
                }
              ];
            });
          }
        },
        onComplete: (info) => {
          console.log('[ChatSidebar] onComplete called with info:', info);
          setShowTyping(false);
          const text = info?.lastText || '';
          console.log('[ChatSidebar] final text to append:', text);
          setMessages(prev => {
            const last = prev[prev.length - 1];
            // If we already have a streaming AI message, finalize it.
            if (last && last.type === 'ai' && last.streaming) {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, streaming: false };
              return updated;
            }
            // Otherwise, append the final text if present.
            if (text) {
              return [...prev, { id: Date.now() + 3, type: 'ai', content: text, timestamp: Date.now() }];
            }
            return prev;
          });
          setActiveStream(null);
        },
        onError: (err) => {
          console.error('[ChatSidebar] onError called:', err);
          setShowTyping(false);
          setActiveStream(null);
          if (!hasStreamedContent) {
            setMessages(prev => [...prev, { id: Date.now() + 2, type: 'ai', content: 'Stream ended without content.', timestamp: Date.now() }]);
          }
        }
        });
      } else {
        console.log('[ChatSidebar] Using standard EventSource stream');
        const openStream = activeMode === 'deep_research' ? openResearchStream : openChatStream;
        es = openStream(streamUrl, {
        onOpen: () => {
          console.log('[ChatSidebar] onOpen called');
        },
        onToken: (data) => {
          console.log('[ChatSidebar] onToken called with data:', data);
          const tokenText = data?.text ?? data?.delta ?? data?.content ?? data?.message ?? '';
          console.log('[ChatSidebar] extracted tokenText:', tokenText);
          const isThoughtToken = !!(data?.thought === true || data?.type === 'thought' || data?.channel === 'thought' || data?.kind === 'thought' || data?.role === 'system_thought');

          if (activeMode === 'deep_research' && !answerStarted && isThoughtToken) {
            if (!tokenText) return;
            setThoughtBuffer(prev => prev + tokenText);
            setShowThoughts(true);
            return;
          }

          if (tokenText) {
            setHasStreamedContent(true);
            if (!answerStarted) {
              setAnswerStarted(true);
              setShowThoughts(false);
            }
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last && last.type === 'ai' && last.streaming) {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  ...last,
                  content: (last.content || '') + tokenText
                };
                return updated;
              }
              return [
                ...prev,
                {
                  id: Date.now() + 1,
                  type: 'ai',
                  content: tokenText,
                  timestamp: Date.now(),
                  streaming: true
                }
              ];
            });
          }
        },
        onComplete: (info) => {
          console.log('[ChatSidebar] onComplete called with info:', info);
          setShowTyping(false);
          const text = info?.lastText || '';
          console.log('[ChatSidebar] final text to append:', text);
          setMessages(prev => {
            const last = prev[prev.length - 1];
            // If we already have a streaming AI message, finalize it.
            if (last && last.type === 'ai' && last.streaming) {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, streaming: false };
              return updated;
            }
            // Otherwise, append the final text if present.
            if (text) {
              return [...prev, { id: Date.now() + 3, type: 'ai', content: text, timestamp: Date.now() }];
            }
            return prev;
          });
          setActiveStream(null);
        },
        onError: (err) => {
          console.error('[ChatSidebar] onError called:', err);
          setShowTyping(false);
          setActiveStream(null);
          if (!hasStreamedContent) {
            setMessages(prev => [...prev, { id: Date.now() + 2, type: 'ai', content: 'Stream ended without content.', timestamp: Date.now() }]);
          }
        }
        });
      }

      setActiveStream(es);
    } catch (err) {
      setShowTyping(false);
      // Do not append an error message here; the stream may still deliver content.
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <Fab
          color="primary"
          aria-label="open chat"
          onClick={handleToggle}
          sx={{
            position: 'fixed',
            top: '80px',
            right: '24px',
            zIndex: theme.zIndex.drawer + 1
          }}
        >
          <MessageOutlined />
        </Fab>
      )}

      {!isFullscreen && (
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={handleToggle}
          variant="persistent"
          sx={{
            '& .MuiDrawer-paper': {
              width: 400,
              height: '100%',
              boxShadow: theme.customShadows.z1,
              borderLeft: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Typography variant="h5">Chat</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  color={activeMode === 'deep_research' ? 'primary' : 'default'}
                  variant={activeMode === 'deep_research' ? 'filled' : 'outlined'}
                  label="Deep Research"
                  onClick={() => setActiveMode(prev => (prev === 'deep_research' ? 'chat' : 'deep_research'))}
                />
                <Chip
                  size="small"
                  color={activeMode === 'chat' ? 'secondary' : 'default'}
                  variant={activeMode === 'chat' ? 'filled' : 'outlined'}
                  label="Chat"
                  onClick={() => setActiveMode('chat')}
                />
                <Button size="small" variant={activeMode === 'agent' ? 'contained' : 'outlined'} onClick={() => setActiveMode('agent')}>
                  Agent
                </Button>
                <Button size="small" variant={activeMode === 'agent_stream' ? 'contained' : 'outlined'} onClick={() => setActiveMode('agent_stream')}>
                  Agent (Streaming)
                </Button>
                {activeMode === 'chat' && (
                  <Chip
                    size="small"
                    color={isStreaming ? 'secondary' : 'default'}
                    variant={isStreaming ? 'filled' : 'outlined'}
                    label={isStreaming ? 'Streaming: On' : 'Streaming: Off'}
                    onClick={() => setIsStreaming(prev => !prev)}
                  />
                )}
              </Stack>
            <Box>
                <Tooltip title="Fullscreen">
                  <IconButton onClick={toggleFullscreen} sx={{ mr: 1 }}>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={handleToggle}>
                  <CloseOutlined />
                </IconButton>
              </Box>
            </Box>
            {(chatContexts.length > 0 || chatContext) && (
              <Alert
                severity="info"
                onClose={() => {
                  dispatch(setChatContext(null));
                  dispatch(clearChatContexts());
                }}
                sx={{ m: 2, mt: 0 }}
              >
                <Typography variant="caption" display="block" gutterBottom>
                  <b>{chatContexts.length > 0 ? `${chatContexts.length} Contexts Selected` : 'Context Selected'}:</b>
                </Typography>
                <Typography variant="caption">
                  Click X to clear all contexts
                </Typography>
              </Alert>
            )}
            <SimpleBar sx={{ flexGrow: 1, p: 2 }}>
              {activeMode === 'deep_research' && (showTyping || (!answerStarted && thoughtBuffer)) && (
                <Card sx={{ mb: 2, backgroundColor: theme.palette.grey[100] }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">
                        {answerStarted ? 'Model thoughts (collapsed)' : 'Model is thinking...'}
                      </Typography>
                      <Chip size="small" label={showThoughts ? 'Hide' : 'Show'} onClick={() => setShowThoughts(prev => !prev)} />
                    </Box>
                    {showThoughts && !answerStarted && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {thoughtBuffer}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
              {chatContexts.map((context) => (
                <ContextMessage key={context.id} context={context} theme={theme} onRemove={handleRemoveContext} />
              ))}
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} theme={theme} />
              ))}
              <div ref={messagesEndRef} />
            </SimpleBar>
            <Box sx={{ p: 0, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
              <Box sx={{ px: 2, py: 1 }}>
                <PremiumChatInput
                  onSendMessage={handleSendMessage}
                  placeholder="Ask anything..."
                  showTyping={showTyping}
                  autoFocus
                />
              </Box>
            </Box>
          </Box>
        </Drawer>
      )}

      {isFullscreen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            bgcolor: theme.palette.background.default,
            zIndex: theme.zIndex.modal + 3,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography variant="h5">Chat</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color={activeMode === 'deep_research' ? 'primary' : 'default'}
                variant={activeMode === 'deep_research' ? 'filled' : 'outlined'}
                label="Deep Research"
                onClick={() => setActiveMode(prev => (prev === 'deep_research' ? 'chat' : 'deep_research'))}
              />
              <Chip
                size="small"
                color={activeMode === 'chat' ? 'secondary' : 'default'}
                variant={activeMode === 'chat' ? 'filled' : 'outlined'}
                label="Chat"
                onClick={() => setActiveMode('chat')}
              />
              <Button size="small" variant={activeMode === 'agent' ? 'contained' : 'outlined'} onClick={() => setActiveMode('agent')}>
                Agent
              </Button>
              <Button size="small" variant={activeMode === 'agent_stream' ? 'contained' : 'outlined'} onClick={() => setActiveMode('agent_stream')}>
                Agent (Streaming)
              </Button>
              {activeMode === 'chat' && (
                <Chip
                  size="small"
                  color={isStreaming ? 'secondary' : 'default'}
                  variant={isStreaming ? 'filled' : 'outlined'}
                  label={isStreaming ? 'Streaming: On' : 'Streaming: Off'}
                  onClick={() => setIsStreaming(prev => !prev)}
                />
              )}
            </Stack>
            <Box>
              <Tooltip title="Exit Fullscreen">
                <IconButton onClick={toggleFullscreen} sx={{ mr: 1 }}>
                  <FullscreenExitIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleToggle}>
                <CloseOutlined />
              </IconButton>
            </Box>
          </Box>
          {(chatContexts.length > 0 || chatContext) && (
            <Alert
              severity="info"
              onClose={() => {
                dispatch(setChatContext(null));
                dispatch(clearChatContexts());
              }}
              sx={{ m: 2, mt: 0 }}
            >
              <Typography variant="caption" display="block" gutterBottom>
                <b>{chatContexts.length > 0 ? `${chatContexts.length} Contexts Selected` : 'Context Selected'}:</b>
              </Typography>
              <Typography variant="caption">
                Click X to clear all contexts
              </Typography>
            </Alert>
          )}
          <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ maxWidth: '1200px', width: '100%', mx: 'auto', px: 3, py: 2, flexGrow: 1 }}>
              {chatContexts.map((context) => (
                <ContextMessage key={context.id} context={context} theme={theme} onRemove={handleRemoveContext} />
              ))}
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} theme={theme} />
              ))}
              <div ref={messagesEndRef} />
            </Box>
          </Box>
          <Box sx={{ p: 0, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
            <Box sx={{ maxWidth: '1200px', width: '100%', mx: 'auto', px: 3, py: 2 }}>
              <PremiumChatInput
                onSendMessage={handleSendMessage}
                placeholder="Ask anything..."
                showTyping={showTyping}
                autoFocus
                actionsPlacement="right"
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ChatSidebar; 